<template>
  <ui-dialog
    :show="isOpen"
    persistent
    scrollable
    max-width="623"
    @input="close"
  >
    <template #header>
      <h3>
        {{
          $t("message.businessAddresses.groups.facilities.addFacilitiesToGroup")
        }}
      </h3>
      <SelectedFacilitiesGroup class="mt-3" />
      <v-text-field
        v-model="facilitiesSearchInputValue"
        :placeholder="
          $t(
            'message.businessAddresses.groups.facilities.filterInputPlaceholder'
          )
        "
        prepend-inner-icon="icon-search"
        clearable
        outlined
        rounded
        class="mt-4 mb-4"
        :hide-details="true"
        @input="onFacilitySearch"
      ></v-text-field>
      <v-row no-gutters v-if="hasFacilities" class="align-center mb-1">
        <v-col>
          <Pagination
            class="flex-grow-1"
            :page="page"
            :per-page="perPage"
            :options="perPageOptions"
            :total-count="totalCount"
            @change="handleChange"
          ></Pagination>
        </v-col>
      </v-row>
    </template>
    <template #default>
      <div class="add-facilities-to-group">
        <v-list two-line v-if="hasFacilities">
          <v-list-item
            ripple
            class="list-item"
            @click="toggleAllFacilities"
            :class="{
              disabled: isAllFacilitiesInitiallySelected
            }"
          >
            <v-checkbox
              :inputValue="isAllFacilitiesSelected"
              :disabled="isAllFacilitiesInitiallySelected"
            ></v-checkbox>
            <v-list-item-content
              class="d-flex align-center justify-space-between"
            >
              <v-list-item-title class="mb-0">
                <p>
                  {{ $t("message.common.selectAll") }}
                </p>
              </v-list-item-title>

              <ItemsCounter :count="selectedFacilitiesCount"></ItemsCounter>
            </v-list-item-content>
          </v-list-item>
          <v-virtual-scroll
            :items="currentPageFacilities"
            :item-height="listItemHeight"
            :height="listHeight"
          >
            <template v-slot:default="{ item }">
              <v-list-item
                :key="item.id"
                ripple
                @click="toggleFacility(item)"
                class="list-item"
                :class="{
                  disabled: isFacilityInitiallySelected(item.id)
                }"
              >
                <v-checkbox
                  :inputValue="isFacilitySelected(item.id)"
                  :disabled="isFacilityInitiallySelected(item.id)"
                ></v-checkbox>
                <IsOwnerIndicator
                  :isOwner="item.facilityListings.isOwner"
                  class="ml-1"
                />
                <v-list-item-content class="d-flex flex-column justify-center">
                  <v-list-item-title>
                    <p class="list-item__name">{{ item.name }}</p>
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    <p class="list-item__description">
                      {{ item.formattedAddress }}
                    </p>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-virtual-scroll>
        </v-list>
        <no-data v-else></no-data>
      </div>
      <SectionLoader :status="loadingList" />
    </template>
    <template #actions>
      <ui-button outlined @click="close">
        {{ $t("message.common.close") }}
      </ui-button>
      <ui-button
        color="primary"
        @click="submit"
        :disabled="!selectedFacilitiesCount"
      >
        {{ $t("message.common.add") }}
      </ui-button>
    </template>
  </ui-dialog>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";
import { debounce } from "@/helpers/helpers";
import Pagination from "@/components/List/Pagination";
import SectionLoader from "@/components/Loader/SectionLoader";
import ItemsCounter from "@/components/List/ItemsCounter";
import SelectedFacilitiesGroup from "@/components/BusinessAddresses/SelectedFacilitiesGroup.vue";
import NoData from "@/components/ui/NoData";
import IsOwnerIndicator from "@/components/Permissions/IsOwnerIndicator.vue";

export default {
  name: "AddFacilitiesToGroupDialog",
  components: {
    Pagination,
    SectionLoader,
    ItemsCounter,
    SelectedFacilitiesGroup,
    NoData,
    IsOwnerIndicator
  },
  props: {
    isOpen: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      page: 1,
      perPage: null,
      perPageOptions: [10, 50, 100, 200],
      facilitiesSearchInputValue: "",
      loading: false,
      loadingList: false,
      listItemHeight: 61
    };
  },
  computed: {
    ...mapState({
      selectedGroup: state => state.facilitiesGroups.selectedGroup || null,
      allSelectedGroupFacilitiesIds: state =>
        state.facilitiesGroups.allSelectedGroupFacilitiesIds,
      selectedFacilities: state => state.facilities.selectedFacilities,
      currentPageFacilities: state => state.facilities.currentPageFacilities,
      totalCount: state => state.facilities.totalCount
    }),
    ...mapGetters({
      isAllFacilitiesSelected: "facilities/isAllFacilitiesSelected",
      isAllCurrentPageFacilitiesSelected:
        "facilities/isAllCurrentPageFacilitiesSelected",
      isAllFacilitiesInitiallySelected:
        "facilities/isAllFacilitiesInitiallySelected",
      selectedFacilitiesCount: "facilities/selectedFacilitiesCount",
      isFacilitySelected: "facilities/isFacilitySelected",
      isFacilityInitiallySelected: "facilities/isFacilityInitiallySelected"
    }),
    hasFacilities() {
      return this.currentPageFacilities.length > 0;
    },
    listHeight() {
      return this.listItemHeight * 6;
    }
  },
  watch: {
    async isOpen(newValue) {
      if (newValue) {
        this.loadingList = true;
        await this.fetchFacilities();
        await this.selectFacilities(this.allSelectedGroupFacilitiesIds);

        this.selectInitiallySelectedFacilities(this.selectedFacilities);
        this.loadingList = false;
      } else {
        this.facilitiesSearchInputValue = "";
        this.selectInitiallySelectedFacilities([]);
      }
    }
  },
  created() {
    this.perPage = this.perPageOptions[0];
  },
  methods: {
    ...mapActions({
      resetStore: "facilities/resetStore",
      toggleFacility: "facilities/toggleFacility",
      selectFacilities: "facilities/selectFacilities",
      toggleAllFacilities: "facilities/toggleAllFacilities",
      selectInitiallySelectedFacilities:
        "facilities/selectInitiallySelectedFacilities"
    }),

    close() {
      this.$emit("close");
      this.page = 1;
    },

    onFacilitySearch: debounce(async function() {
      this.loadingList = true;
      this.page = 1;
      await this.fetchFacilities();
      this.loadingList = false;
    }, 500),

    fetchFacilities() {
      const params = {
        page: this.page,
        perPage: this.perPage,
        query: this.facilitiesSearchInputValue,
        isOwner: true,
        fetchAllFacilitiesIds: true
      };

      return this.$store.dispatch("facilities/fetchFacilities", params);
    },

    async handleChange({ page, perPage }) {
      this.page = page;
      this.perPage = perPage;
      this.loadingList = true;
      await this.fetchFacilities();
      this.loadingList = false;
    },

    async submit() {
      this.save();
    },

    save() {
      this.$emit("add");
      this.close();
    }
  }
};
</script>

<style lang="scss">
.add-facilities-to-group {
  margin-bottom: $spacing * 3;
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  .v-list-item {
    &__title,
    &__subtitle {
      align-self: flex-start;
      white-space: unset;

      p {
        margin-bottom: 0;
      }
    }

    &__content {
      padding-left: $spacing * 1.5;

      * {
        flex: unset;
      }

      .selected-counter {
        line-height: 19px;
      }
    }

    &.disabled {
      opacity: 0.5;
    }
  }
}
</style>
